/**
 * Générateur de mots de passe et de phrases de passe — fine couche UI au-dessus
 * de `@klavox/crypto-core` (aucune logique cryptographique ici : tirage et
 * entropie restent dans le cœur, qui valide aussi les listes de mots).
 */
import {
  generatePassphrase,
  generatePassword,
  passphraseEntropyBits,
  passwordEntropyBits,
  type PasswordGeneratorOptions,
} from '@klavox/crypto-core';

export const DEFAULT_GENERATOR_OPTIONS: Required<
  Pick<PasswordGeneratorOptions, 'length' | 'lowercase' | 'uppercase' | 'digits' | 'symbols' | 'excludeAmbiguous'>
> = {
  length: 20,
  lowercase: true,
  uppercase: true,
  digits: true,
  symbols: true,
  excludeAmbiguous: false,
};

export interface GeneratedPassword {
  readonly password: string;
  readonly entropyBits: number;
}

export function generate(options: PasswordGeneratorOptions = DEFAULT_GENERATOR_OPTIONS): GeneratedPassword {
  return {
    password: generatePassword(options),
    entropyBits: passwordEntropyBits(options),
  };
}

export type WordlistLanguage = 'fr' | 'en';

export interface PassphraseSettings {
  readonly language: WordlistLanguage;
  readonly words: number;
  readonly separator: string;
  readonly capitalize: boolean;
  readonly includeNumber: boolean;
}

export const MIN_PASSPHRASE_WORDS = 3;
export const MAX_PASSPHRASE_WORDS = 20;

/** 6 mots sur 7776 = 77,5 bits (recommandation EFF), hors chiffre. */
export const DEFAULT_PASSPHRASE_SETTINGS: PassphraseSettings = {
  language: 'fr',
  words: 6,
  separator: '-',
  capitalize: true,
  includeNumber: true,
};

export const PASSPHRASE_SEPARATORS: readonly { readonly value: string; readonly label: string }[] = [
  { value: '-', label: 'Tiret (-)' },
  { value: '.', label: 'Point (.)' },
  { value: '_', label: 'Trait bas (_)' },
  { value: ',', label: 'Virgule (,)' },
  { value: ' ', label: 'Espace' },
];

const loaded = new Map<WordlistLanguage, Promise<readonly string[]>>();

/**
 * Charge une liste embarquée (morceau séparé du bundle, chargé à la demande :
 * aucune requête hors de l'origine de l'application). Un échec n'est pas mis
 * en cache, pour pouvoir réessayer.
 */
export function loadWordlist(language: WordlistLanguage): Promise<readonly string[]> {
  let promise = loaded.get(language);
  if (promise === undefined) {
    const source = language === 'fr' ? import('./wordlists/diceware-fr.txt?raw') : import('./wordlists/eff-large-en.txt?raw');
    promise = source.then((module) => Object.freeze(module.default.split(/\r?\n/u).filter((word) => word.length > 0)));
    promise.catch(() => loaded.delete(language));
    loaded.set(language, promise);
  }
  return promise;
}

export function generatePassphraseWith(wordlist: readonly string[], settings: PassphraseSettings): GeneratedPassword {
  const options = { wordlist, ...settings };
  return {
    password: generatePassphrase(options),
    // Borne basse : le chiffre optionnel n'est pas compté.
    entropyBits: passphraseEntropyBits(options),
  };
}

export function strengthLabel(entropyBits: number): 'faible' | 'correcte' | 'forte' | 'excellente' {
  if (entropyBits < 40) return 'faible';
  if (entropyBits < 60) return 'correcte';
  if (entropyBits < 80) return 'forte';
  return 'excellente';
}
